'use client';

import { useEffect, useState } from 'react';

interface MindmapEdgeEntranceStartInput {
  edgeId: string;
  isEntranceComplete: boolean;
}

interface MindmapEdgeEntranceScheduleInput {
  isEntranceComplete: boolean;
  setDidEntranceStart: (didEntranceStart: boolean) => void;
}

type MindmapEdgeEntranceCleanup = () => void;

const EMPTY_ENTRANCE_CLEANUP: MindmapEdgeEntranceCleanup = () => undefined;

export function useMindmapEdgeEntranceStart({
  edgeId,
  isEntranceComplete,
}: MindmapEdgeEntranceStartInput): boolean {
  const [didEntranceStart, setDidEntranceStart] = useState<boolean>(isEntranceComplete);

  useEffect(
    () =>
      scheduleMindmapEdgeEntranceStart({
        isEntranceComplete,
        setDidEntranceStart,
      }),
    [edgeId, isEntranceComplete],
  );

  return didEntranceStart;
}

function scheduleMindmapEdgeEntranceStart(
  input: MindmapEdgeEntranceScheduleInput,
): MindmapEdgeEntranceCleanup {
  if (typeof window === 'undefined') {
    return EMPTY_ENTRANCE_CLEANUP;
  }

  return input.isEntranceComplete
    ? scheduleCompletedMindmapEdgeEntrance(input)
    : scheduleRestartedMindmapEdgeEntrance(input);
}

function scheduleCompletedMindmapEdgeEntrance(
  input: MindmapEdgeEntranceScheduleInput,
): MindmapEdgeEntranceCleanup {
  const firstFrame = window.requestAnimationFrame(() => {
    input.setDidEntranceStart(true);
  });

  return () => {
    window.cancelAnimationFrame(firstFrame);
  };
}

function scheduleRestartedMindmapEdgeEntrance(
  input: MindmapEdgeEntranceScheduleInput,
): MindmapEdgeEntranceCleanup {
  let secondFrame: number | null = null;
  const firstFrame = window.requestAnimationFrame(() => {
    input.setDidEntranceStart(false);
    secondFrame = window.requestAnimationFrame(() => {
      input.setDidEntranceStart(true);
    });
  });

  return () => {
    window.cancelAnimationFrame(firstFrame);

    if (secondFrame != null) {
      window.cancelAnimationFrame(secondFrame);
    }
  };
}
